import { permisosPrueba } from "../lib/pruebaGratis";
import React, { createContext, useContext, useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";
import { usePlanActual } from "../lib/planes";

// PUENTE-6/7: cuenta real de no_leidos (agency), para el badge del menú lateral.
// Vive fuera de Inbox.js a propósito -- el badge debe verse desde cualquier pantalla del
// panel, no solo mientras Inbox está montado. Demo mode: no_leidos no existe en la tabla
// local, así que aquí nunca se inventa -- unreadTotal se queda en 0 y no se hace polling.
//
// PUENTE-8 PASO 6: Inbox.js ya pide GET /whatsapp/conversations cada 8s por su cuenta -- pedirlo
// AQUÍ TAMBIÉN cada 15s duplica el mismo endpoint sin necesidad mientras Inbox está montado.
// `reportConversations()` deja que Inbox alimente este total con los datos que YA trajo, sin
// una segunda petición. `setExternallyFed(true)` no detiene el interval (sigue el mismo timer,
// sin costo real) -- solo hace que `poll()` no dispare la llamada de red mientras haya un
// "alimentador" activo. Al desmontarse Inbox, `setExternallyFed(false)` deja que el próximo
// tick del interval (máximo 15s después) vuelva a pedir por su cuenta, sin fricción.
const WhatsAppUnreadContext = createContext({
  unreadTotal: 0,
  reportConversations: () => {},
  setExternallyFed: () => {},
});

const POLL_MS = 15000;

function sumUnread(list) {
  return (list || []).reduce((sum, c) => sum + (c.no_leidos || 0), 0);
}

export function WhatsAppUnreadProvider({ children }) {
  const { user } = useAuth();
  // TEN.4: un plan sin Sofi no tiene bandeja -- sondear cada 15 s solo golpeaba a agency para
  // recibir un 404. Se sondea cuando el plan CONFIRMA Sofi (o es antiguo, o el catalogo falla:
  // como hasta hoy). Mientras se consulta el plan, no se sondea.
  const planActual = usePlanActual();
  const sondear = planActual.estado !== "cargando" && !planActual.sinSofi && user?.plan_vigente?.vigente !== false && permisosPrueba(user)?.puede_operar !== false;
  const isDemo = !!user?.is_demo_session;
  const [unreadTotal, setUnreadTotal] = useState(0);
  const fedExternallyRef = useRef(false);

  const poll = useCallback(async () => {
    if (isDemo || !user || !sondear || fedExternallyRef.current) return;
    try {
      const { data } = await api.get("/whatsapp/conversations");
      setUnreadTotal(sumUnread(data.items));
    } catch {
      // el badge no es crítico -- Inbox.js ya reporta la pérdida de conexión real
    }
  }, [isDemo, user, sondear]);

  useEffect(() => {
    if (isDemo || !user || !sondear) {
      setUnreadTotal(0);
      return;
    }
    poll();
    let interval = setInterval(() => {
      if (document.visibilityState === "visible") poll();
    }, POLL_MS);
    const onVisibility = () => {
      if (document.visibilityState === "visible") poll();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [isDemo, user, sondear, poll]);

  const reportConversations = useCallback((list) => {
    setUnreadTotal(sumUnread(list));
  }, []);

  const setExternallyFed = useCallback((fed) => {
    fedExternallyRef.current = fed;
  }, []);

  return (
    <WhatsAppUnreadContext.Provider value={{ unreadTotal, reportConversations, setExternallyFed }}>
      {children}
    </WhatsAppUnreadContext.Provider>
  );
}

export function useWhatsAppUnread() {
  return useContext(WhatsAppUnreadContext);
}
